import {
  formatDiscountAmount,
  formatCurrency,
  getItemName,
} from "@sellar-io/common";
import { Button, Card, H5, Icon } from "@sellar-io/components";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { ProfilePicture } from "../../components";
import { setFallbackImage } from "../../components/image/imageWithFallback.shared";
import {
  DISCOVER_SEARCH,
  UNIFIED_CHECKOUT_BASKET_DETAILS,
} from "../../constants/routes";
import { BasketOrderItem, ContextOrderBasketType } from "../../hooks";
import { isPromotionalOrderItem } from "../stockList/categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";
import { NavigationBar } from "../../components/navigation/NavigationBar";
import { useUnifiedCheckout } from "./UnifiedCheckoutProvider.shared";
import { Badge } from "../../components/badge/Badge";
import {
  useUnifiedCheckoutBasket,
  useUnifiedCheckoutBasketUpdateItem,
} from "../../hooks/api/v2/unified-checkout-basket/useUnifiedCheckoutBaskets";
import { useDiscoveryV2SearchStore } from "../discovery/v2/DiscoveryV2SearchStore";
import { SupplierKegBadges } from "./components/SupplierKegBadges";
import { SupplierPromotions } from "./components/SupplierPromotions";
import type { SupplierOrderEntry } from "./components/UnifiedCheckoutOverviewPage.shared";
import { useUnifiedCheckoutTracking } from "./useUnifiedCheckoutTracking";
import { useConsolidatedOrderingIntercom } from "../../hooks/useConsolidatedOrderingIntercom";
import { useMarketStorefrontSessionId } from "../../hooks/useMarketStorefrontSessionTracking";
import { getConsolidatedBasketIntercomData } from "../discovery/v2/distribution/consolidatedOrderingTracking";
import { UnifiedCheckoutDeliveryDateSummary } from "./components/UnifiedCheckoutDeliveryDateSummary";
import { calculateUnifiedCheckoutOrderPricing } from "./lib/calculateUnifiedCheckoutOrderPricing";
import { getIncompleteUnifiedCheckoutCaseSizeRules } from "./lib/getIncompleteUnifiedCheckoutCaseSizeRules";
import { UnifiedCheckoutCaseSizeRuleStatus } from "./components/UnifiedCheckoutCaseSizeRuleStatus";

// ─── Qty stepper ──────────────────────────────────────────────────────────────

const QtyControl = ({
  item,
  onChange,
  disabled,
}: {
  item: BasketOrderItem;
  onChange: (_item: BasketOrderItem, _delta: number) => void;
  disabled?: boolean;
}) => (
  <div className="flex items-center gap-2">
    <Button
      size="small"
      className="p-2.5"
      disabled={disabled}
      onClick={() => onChange(item, -1)}
    >
      <Icon icon="MINUS" size={0.6} />
    </Button>
    <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-300 bg-white text-sm font-medium">
      {item.quantity}
    </div>
    <Button
      size="small"
      className="p-2.5"
      disabled={disabled}
      onClick={() => onChange(item, 1)}
    >
      <Icon icon="PLUS" size={0.6} />
    </Button>
  </div>
);

// ─── Single line-item row ───────────────────────────────────────────────────

const LineItem = ({
  item,
  order,
  onChange,
  disabled,
}: {
  item: BasketOrderItem;
  order: ContextOrderBasketType;
  onChange: (item: BasketOrderItem, delta: number) => void;
  disabled?: boolean;
}) => {
  const product = item.Product;
  const [parentName, variantName] = (product.name ?? "")
    .split("•")
    .map((s) => s.trim());

  const heroImageUrl = setFallbackImage({
    img: product.Parent?.heroImageUrl ?? product.heroImageUrl ?? null,
    fallback: "product-hero",
  });

  const currency = order.currency ?? "gbp";

  return (
    <div className="flex items-center gap-3">
      {/* Product thumbnail */}
      <div
        className="h-13 w-13 shrink-0 overflow-hidden rounded-lg bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${heroImageUrl})` }}
      />

      {/* Name and description */}
      <div className="flex flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <H5 className="font-semibold text-zinc-900">{parentName}</H5>
          {isPromotionalOrderItem(item) && (
            <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
              Promotion
            </span>
          )}
        </div>
        {variantName && (
          <span className="text-sm text-zinc-600">{variantName}</span>
        )}
      </div>

      {/* Price and qty stepper */}
      <div className="flex flex-col items-end gap-1.5">
        <span className="font-semibold text-zinc-900">
          {formatCurrency(item.price, currency)}
        </span>
        {isPromotionalOrderItem(item) ? (
          <span className="text-sm text-zinc-600">Qty: {item.quantity}</span>
        ) : (
          <QtyControl item={item} onChange={onChange} disabled={disabled} />
        )}
      </div>
    </div>
  );
};

// ─── Supplier order card ──────────────────────────────────────────────────────

const SupplierOrderCard = ({
  entry,
  onItemChange,
  isUpdating,
}: {
  entry: SupplierOrderEntry;
  onItemChange: (item: BasketOrderItem, quantity: number) => void;
  isUpdating: boolean;
}) => {
  const navigate = useNavigate();
  const setSelectedSuppliers = useDiscoveryV2SearchStore(
    (state) => state.setSelectedSuppliers,
  );
  const setIsDeliveredBySellar = useDiscoveryV2SearchStore(
    (state) => state.setIsDeliveredBySellar,
  );
  const { order, supplierInfo } = entry;
  const items = order.OrderItems ?? [];
  const hasPurchasableItems = items.some(
    (item) => !isPromotionalOrderItem(item),
  );
  const { total, appliedVolumeDiscounts } =
    calculateUnifiedCheckoutOrderPricing({
      orderItems: items,
      storefront: supplierInfo.storefront,
      vatPercentage: order.vatPercentage ?? 0,
    });
  const incompleteCaseSizeRules = getIncompleteUnifiedCheckoutCaseSizeRules({
    orderItems: items,
    storefront: supplierInfo.storefront,
  });

  if (items.length === 0) return null;

  const handleChange = (item: BasketOrderItem, delta: number) =>
    onItemChange(item, Math.max(0, item.quantity + delta));

  const handleMoreFromSupplier = () => {
    setIsDeliveredBySellar(true);
    setSelectedSuppliers([
      {
        id: supplierInfo.supplier.id,
        name: supplierInfo.supplier.Business.name,
      },
    ]);
    navigate(DISCOVER_SEARCH);
  };

  return (
    <Card className="overflow-hidden rounded-2xl border border-zinc-200 bg-white p-0 shadow-sm">
      {/* Supplier header */}
      <div className="flex items-center justify-between p-4">
        <Badge className={"text-sm font-medium"}>
          <span className="flex items-center gap-2">
            <ProfilePicture
              size={"small"}
              logoUrl={supplierInfo.supplier.Business.logoUrl}
              fallback="supplier-badge"
            />
            {supplierInfo.supplier.Business.name}
          </span>
        </Badge>

        {hasPurchasableItems && (
          <button
            type="button"
            className="hidden items-center gap-1 text-sm font-semibold text-purple-600 transition-colors hover:text-purple-700 sm:flex"
            onClick={handleMoreFromSupplier}
          >
            More from {supplierInfo.supplier.Business.name}
            <Icon icon="CHEVRON_RIGHT" size={0.7} />
          </button>
        )}
      </div>

      {/* Line items list */}
      <div className="flex flex-col gap-2 p-4 pt-0">
        {items.map((item) => (
          <LineItem
            key={item.id ?? `${item.ProductId}-${item.PromotionRewardItemId}`}
            item={item}
            order={order}
            onChange={handleChange}
            disabled={isUpdating}
          />
        ))}
        {hasPurchasableItems && (
          <button
            type="button"
            className="mt-2 flex items-center justify-end gap-1 text-sm font-semibold text-purple-600 transition-colors hover:text-purple-700 sm:hidden"
            onClick={handleMoreFromSupplier}
          >
            More from {supplierInfo.supplier.Business.name}
            <Icon icon="CHEVRON_RIGHT" size={0.6} />
          </button>
        )}
      </div>
      {appliedVolumeDiscounts.length > 0 && (
        <div className="flex flex-col gap-2 px-4 pb-4">
          {appliedVolumeDiscounts.map(({ volumeDiscount, discountAmount }) => (
            <div
              key={volumeDiscount.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-purple-300 bg-purple-50 px-3 py-2 text-sm font-semibold text-purple-700"
            >
              <span className="flex items-center gap-2">
                <Icon icon="CHECK_CIRCLE" size={0.65} />
                {formatDiscountAmount(
                  volumeDiscount,
                  order.currency ?? "gbp",
                )}{" "}
                off{" "}
                {getItemName({
                  itemType: volumeDiscount.itemType,
                  quantity: 2,
                  extended: true,
                })}{" "}
                applied!
              </span>
              <span className="shrink-0">
                -{formatCurrency(discountAmount, order.currency ?? "gbp")}
              </span>
            </div>
          ))}
        </div>
      )}
      {incompleteCaseSizeRules.length > 0 && (
        <div className="px-4 pb-4">
          <UnifiedCheckoutCaseSizeRuleStatus
            supplierName={supplierInfo.supplier.Business.name}
            rules={incompleteCaseSizeRules}
          />
        </div>
      )}
      <div className="flex items-center justify-between border-t border-zinc-200 px-4 py-3 font-semibold">
        <span>Supplier total</span>
        <span>{formatCurrency(total, order.currency ?? "gbp")}</span>
      </div>
    </Card>
  );
};

// ─── Overview page ───────────────────────────────────────────────────────────

export const UnifiedCheckoutOverviewPage = () => {
  const navigate = useNavigate();
  const { sdBasket, sdOrders, supplierInfoMap, isLoading } =
    useUnifiedCheckout();
  const { trackStepViewed } = useUnifiedCheckoutTracking();
  const trackOverviewViewed = useEffectEvent(() => trackStepViewed("overview"));
  useEffect(() => {
    trackOverviewViewed();
  }, []);
  const basketQuery = useUnifiedCheckoutBasket(sdBasket.uuid);
  const updateItem = useUnifiedCheckoutBasketUpdateItem();
  const { trackBasketUpdated } = useConsolidatedOrderingIntercom();
  const session = useMarketStorefrontSessionId();
  const [extrasAvailability, setExtrasAvailability] = useState<
    Record<string, boolean>
  >({});
  const setExtraAvailability = useCallback(
    (key: string, available: boolean) => {
      setExtrasAvailability((current) =>
        current[key] === available ? current : { ...current, [key]: available },
      );
    },
    [],
  );
  const hasOptionalExtras = Object.values(extrasAvailability).some(Boolean);

  const supplierOrders = useMemo(
    () =>
      sdOrders
        .map((order) => ({
          order,
          supplierInfo: supplierInfoMap.get(order.SupplierId!),
        }))
        .filter((entry): entry is SupplierOrderEntry => {
          if (entry.supplierInfo == null) return false;
          return (entry.order.OrderItems?.length ?? 0) > 0;
        }),
    [sdOrders, supplierInfoMap],
  );

  const handleItemChange = (item: BasketOrderItem, quantity: number) => {
    if (item.id == null) return;
    updateItem.mutate(
      { basketUuid: sdBasket.uuid, itemId: item.id, quantity },
      {
        onSuccess: (updatedBasket) => {
          if (!updatedBasket) return;
          trackBasketUpdated({
            journeyKey: updatedBasket.uuid,
            ...getConsolidatedBasketIntercomData(updatedBasket, session),
          });
        },
      },
    );
  };

  const handleBack = () => navigate(DISCOVER_SEARCH);
  const handleNext = () =>
    navigate(
      UNIFIED_CHECKOUT_BASKET_DETAILS.href({ sdBasketUuid: sdBasket.uuid }),
    );
  const hasPromotionalOnlyOrder = supplierOrders.some((entry) => {
    const items = entry.order.OrderItems ?? [];
    return (
      items.some(isPromotionalOrderItem) && items.every(isPromotionalOrderItem)
    );
  });
  const hasIncompleteCaseSizeRules = supplierOrders.some((entry) =>
    Boolean(
      getIncompleteUnifiedCheckoutCaseSizeRules({
        orderItems: entry.order.OrderItems ?? [],
        storefront: entry.supplierInfo.storefront,
      }).length,
    ),
  );

  const orderSummary = useMemo(() => {
    const currency = sdOrders[0]?.currency ?? "gbp";
    const { subtotal, discount, vat, total } = sdOrders.reduce(
      (summary, order) => {
        const storefront =
          order.SupplierId == null
            ? null
            : supplierInfoMap.get(order.SupplierId)?.storefront;
        const orderPricing = calculateUnifiedCheckoutOrderPricing({
          orderItems: order.OrderItems ?? [],
          storefront,
          vatPercentage: order.vatPercentage ?? 0,
        });

        return {
          subtotal: summary.subtotal + orderPricing.subtotal,
          discount: summary.discount + orderPricing.discount,
          vat: summary.vat + orderPricing.vat,
          total: summary.total + orderPricing.total,
        };
      },
      { subtotal: 0, discount: 0, vat: 0, total: 0 },
    );

    return {
      currency,
      subtotal,
      discount,
      vat,
      total,
    };
  }, [sdOrders, supplierInfoMap]);

  return (
    <div className="flex min-h-screen flex-col bg-zinc-100">
      <NavigationBar
        title="Your order"
        leftIcon="CHEVRON_LEFT"
        onClickLeftIcon={handleBack}
      />

      <div className="flex-1 overflow-y-auto p-4 pb-32 lg:p-8 lg:pb-32">
        <div className="mx-auto flex w-full max-w-[720px] flex-col gap-4">
          {supplierOrders.length > 0 && (
            <>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold">Review your order</h1>
                <Icon icon="ARROW_DOWN" size={0.8} />
              </div>
              <UnifiedCheckoutDeliveryDateSummary
                deliveryPreference={sdBasket.deliveryPreference}
                consolidatedDeliveryDate={sdBasket.deliveryDate}
                supplierDeliveryDates={sdOrders.map(
                  (order) => order.expectedDeliveryDate,
                )}
                className="text-sm text-purple-600"
              />
            </>
          )}

          {isLoading ? (
            <div className="text-center text-sm text-zinc-500">
              Loading basket...
            </div>
          ) : supplierOrders.length === 0 ? (
            <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 text-center">
              <Icon icon="CART_OUTLINE" size={2} className="text-zinc-400" />
              <div className="flex flex-col gap-2">
                <h2 className="text-xl font-semibold text-zinc-900">
                  Your delivered by Sellar basket is empty
                </h2>
                <p className="text-zinc-500">
                  Head to the Market to build your basket with multiple
                  suppliers all delivered by Sellar
                </p>
              </div>
              <Button onClick={() => navigate(DISCOVER_SEARCH)}>
                GO TO THE MARKET
                <Icon icon="CHEVRON_RIGHT" size={0.8} />
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {supplierOrders.map((entry) => (
                <SupplierOrderCard
                  key={entry.order.SupplierId}
                  entry={entry}
                  onItemChange={handleItemChange}
                  isUpdating={updateItem.isPending}
                />
              ))}
            </div>
          )}

          {supplierOrders.length > 0 && (
            <Card className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex items-center justify-between">
                  <span>Subtotal</span>
                  <span>
                    {formatCurrency(
                      orderSummary.subtotal,
                      orderSummary.currency,
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Delivery</span>
                  <span>FREE</span>
                </div>
                {orderSummary.discount > 0 && (
                  <div className="flex items-center justify-between text-green-700">
                    <span>Volume discount</span>
                    <span>
                      -
                      {formatCurrency(
                        orderSummary.discount,
                        orderSummary.currency,
                      )}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span>VAT</span>
                  <span>
                    {formatCurrency(orderSummary.vat, orderSummary.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-zinc-200 pt-2 font-semibold">
                  <span>Order Total</span>
                  <span>
                    {formatCurrency(orderSummary.total, orderSummary.currency)}
                  </span>
                </div>
              </div>
            </Card>
          )}

          {supplierOrders.length > 0 && hasOptionalExtras && (
            <div className="mt-4">
              <h2 className="text-xl font-semibold">Optional extras</h2>
            </div>
          )}
          {Object.entries(extrasAvailability).some(
            ([key, available]) => key.startsWith("badges-") && available,
          ) && <p className="text-sm text-zinc-600">Keg badges</p>}
          <div className="flex flex-col gap-4">
            {supplierOrders.map((entry) => (
              <SupplierKegBadges
                key={`keg-badges-${entry.order.SupplierId}`}
                entry={entry}
                basketUuid={sdBasket.uuid}
                basketItems={
                  basketQuery.data?.DraftOrders.find(
                    (draft) => draft.SupplierId === entry.order.SupplierId,
                  )?.Items ?? []
                }
                onAvailabilityChange={(available) =>
                  setExtraAvailability(
                    `badges-${entry.order.SupplierId}`,
                    available,
                  )
                }
              />
            ))}
          </div>
          {Object.entries(extrasAvailability).some(
            ([key, available]) => key.startsWith("promotions-") && available,
          ) && (
            <p className="text-sm text-zinc-600">Discounts and promotions</p>
          )}
          <div
            className={
              hasOptionalExtras ? "grid gap-4 md:grid-cols-2" : "hidden"
            }
          >
            {supplierOrders.map((entry) => (
              <SupplierPromotions
                key={`promotions-${entry.order.SupplierId}`}
                entry={entry}
                basketUuid={sdBasket.uuid}
                basketItems={
                  basketQuery.data?.DraftOrders.find(
                    (draft) => draft.SupplierId === entry.order.SupplierId,
                  )?.Items ?? []
                }
                onAvailabilityChange={(available) =>
                  setExtraAvailability(
                    `promotions-${entry.order.SupplierId}`,
                    available,
                  )
                }
              />
            ))}
          </div>

          {supplierOrders.length > 0 && (
            <Button
              className="w-full"
              disabled={hasPromotionalOnlyOrder || hasIncompleteCaseSizeRules}
              onClick={handleNext}
            >
              Next
              <Icon icon="CHEVRON_RIGHT" size={0.8} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
